import * as React from "react";
import { size } from "lodash";
import styled from "styled-components";
import { Color } from "@glossy/colors";
import CodeMirror from "@komon/form/codemirror";
import { Locale } from "@linkfluence/models/locale";
import Loading from "@ui-kit/loader/caterpillar";
import Modal from "@ui-kit/modal";
import QueryPortrait from "@radarly/components/portrait/query";
import { tr } from "@radarly/i18n";
import { Project } from "@radarly/models/project";
import { Query, QueryDetails } from "@radarly/models/queries";
import Block from "./block";
import Parents from "./parents";
import Separator from "./separator";

const Title = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: ${Color.DimGrey};
  & > span {
    padding-left: 6px;
  }
`;

const SubTitle = styled.div`
  padding: 22px;
  text-align: center;
  color: ${Color.DimGrey};
`;

const LoadingContainer = styled.div`
  height: 200px;
  position: relative;
`;

const StyledCodeMirror = styled.div`
  text-align: left;
  .CodeMirror {
    height: auto;
    border: none;
  }
`;

export interface IProps {
  query: QueryDetails;
  locale: Locale;
  project: Project;
  search?: string;
  includes?: Query[];
  excludes?: Query[];
  filters?: React.ReactNode;
  loading?: boolean;
  onCancel: () => void;
}

const QueriesInfoModal = ({
  query,
  project,
  locale,
  onCancel,
  loading,
  search = "",
  includes = [],
  excludes = [],
  filters,
}: IProps) => {
  return (
    <Modal
      cancelLabel={tr(locale, "MODAL::CLOSE_WINDOW")}
      onCancel={onCancel}
      locale={locale}
    >
      {loading ? (
        <LoadingContainer>
          <Loading locale={locale} />
        </LoadingContainer>
      ) : (
        <>
          <Title>
            <QueryPortrait picture={query.imageUrl} borderColor={query.color} />
            <span>{query.label}</span>
          </Title>
          <SubTitle>
            {query.isSource
              ? tr(locale, "QUERY_DETAILS::EXPLAIN_SEARCH")
              : tr(locale, "QUERY_DETAILS::EXPLAIN_FOCUS", {
                  projectLabel: project.label,
                })}
          </SubTitle>
          {!query.isSource && (
            <Parents
              title={tr(locale, "QUERY_DETAILS::INCLUDE_PARENTS")}
              type="include"
              parents={includes}
              locale={locale}
              project={project}
            />
          )}
          {!!size(excludes) && (
            <div>
              <Separator label={"AND NOT"} />
              <Parents
                title={tr(locale, "QUERY_DETAILS::EXCLUDE_PARENTS")}
                type="exclude"
                parents={excludes}
                locale={locale}
                project={project}
              />
            </div>
          )}
          {!!size(search) && (
            <div>
              {!query.isSource && <Separator label={"AND"} />}
              <Block
                title={tr(locale, "QUERY_DETAILS::SEARCH_TERMS")}
                icon="magnifier"
              >
                <StyledCodeMirror>
                  <CodeMirror value={search} options={{ readOnly: true }} />
                </StyledCodeMirror>
              </Block>
            </div>
          )}
          {!!filters && (
            <div>
              {(!!size(search) || !query.isSource) && (
                <Separator label={"AND"} />
              )}
              <Block title={tr(locale, "QUERY_DETAILS::FILTERS")} icon="funnel">
                {filters}
              </Block>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default QueriesInfoModal;
